import {Button, Typography} from "@mui/material";

/**
 * What the user sees when a hand-off from another app could not be exchanged for a session.
 *
 * BEFORE THIS, THEY SAW NOTHING. ssoAuthenticate returns false on a non-200, useSsoLogin fell through
 * to "the normal logged-out landing", and in an app whose logged-out landing is an empty route that
 * is a white screen. The user clicked a link in one app and arrived in another at nothing at all,
 * with no error, no explanation and no way forward -- the failure mode that reads as "the product is
 * broken" rather than "your link timed out".
 *
 * TWO MESSAGES, BECAUSE THERE ARE TWO FAILURES AND THEY ARE NOT THE USER'S IN EQUAL MEASURE. A 401
 * really is a dead link, usually the boring case: the source tab had been open long enough for its
 * access token to expire. A 5xx is ours, and telling somebody their link expired when the exchange
 * crashed sends them to sign in again against a server that will do the same thing -- which is
 * exactly what happened when the authorizer refused every cross-app transfer in the suite. The
 * screen said "expired" and the only evidence was in a log nobody had reason to open.
 *
 * THE SUPPORT CODE IS SHOWN WHEN THERE IS ONE, because on a fault it is the fastest route from
 * "it did not work" to the stack trace that says why.
 */
export default function SsoHandoffFailed(
    {onSignIn, fault, code}: {onSignIn: () => void; fault?: boolean; code?: string | null},
) {
    const title = fault ? "We could not finish signing you in" : "That sign-in link is no longer valid";
    const detail = fault
        ? "Something went wrong on our side while handing your session over -- the link itself is fine. Signing in here takes you straight to the same place."
        : "Links from another app are short-lived, and this one has expired. Signing in here takes you straight to the same place.";

    return (
        <div style={{display: "flex", alignItems: "center", justifyContent: "center", height: "100vh"}}>
            <div style={{maxWidth: 420, textAlign: "center", padding: "0 24px"}}>
                <Typography style={{fontSize: 20, fontWeight: 500, marginBottom: 10}}>
                    {title}
                </Typography>
                <Typography style={{fontSize: 14, color: "#525968", marginBottom: 22}}>
                    {detail}
                </Typography>
                <Button variant="contained" onClick={onSignIn}>Sign in</Button>
                {fault && code ? (
                    <Typography style={{fontSize: 12, color: "#8A91A0", marginTop: 18}}>
                        Reference {code}
                    </Typography>
                ) : null}
            </div>
        </div>
    );
}
